import { type Dispatch, type RefObject, type SetStateAction, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { filterOptions } from "./filterOptions";
import { activeAfterFilter, initialActive } from "./optionNavigation";
import type { SelectOption } from "./selectOption";

interface SelectListInput {
  options: SelectOption[];
  value: string;
  selectedIndex: number;
  disabled?: boolean;
  searchable: boolean;
}

export interface SelectList {
  listId: string;
  open: boolean;
  query: string;
  /** Options shown: all of them, or those matching the query. */
  visible: SelectOption[];
  /** Index in `visible` of the highlighted option, -1 for none. */
  active: number;
  setActive: Dispatch<SetStateAction<number>>;
  triggerRef: RefObject<HTMLButtonElement | null>;
  searchRef: RefObject<HTMLInputElement | null>;
  listRef: RefObject<HTMLUListElement | null>;
  /** Opens with `activeIndex` highlighted (the selected option by default). Ignored while disabled. */
  openList: (activeIndex?: number) => void;
  /** Closes and returns the focus to the trigger. */
  close: () => void;
  /** Closes leaving the focus where it is (Tab, click outside). */
  dismiss: () => void;
  /** Opens with `query` typed in the search box. */
  openWithQuery: (query: string) => void;
  changeQuery: (query: string) => void;
}

/** Open state, search query and highlighted option of the listbox. */
export function useSelectList({ options, value, selectedIndex, disabled, searchable }: SelectListInput): SelectList {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();
  const visible = useMemo(() => (searchable ? filterOptions(options, query) : options), [searchable, options, query]);

  const dismiss = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  const close = useCallback(() => {
    dismiss();
    triggerRef.current?.focus({ preventScroll: true });
  }, [dismiss]);

  function openList(activeIndex = initialActive(options, selectedIndex)): void {
    if (disabled) return;
    setQuery("");
    setActive(activeIndex);
    setOpen(true);
  }

  function openWithQuery(text: string): void {
    openList();
    setQuery(text);
  }

  function changeQuery(text: string): void {
    setQuery(text);
    setActive(activeAfterFilter(options, text, value));
  }

  useEffect(() => {
    if (open && searchable) searchRef.current?.focus({ preventScroll: true });
  }, [open, searchable]);

  // Keep the active option in view while moving with the keyboard.
  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [open, active]);

  return { listId, open, query, visible, active, setActive, triggerRef, searchRef, listRef, openList, close, dismiss, openWithQuery, changeQuery };
}
